import React from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';

/**
 * Simple markdown renderer for CV content
 * Converts markdown to React components
 */
const MarkdownRenderer = ({ markdown }) => {
    if (!markdown) return null;

    // Filter out AI generation disclaimer text
    let filteredMarkdown = markdown;
    const disclaimerPatterns = [
        /^\*?This CV was generated using AI\. Please review and customize as needed\.\*?$/i,
        /^\*?This CV was generated using AI\.\*?$/i,
        /^\*?Generated using AI\. Please review and customize as needed\.\*?$/i,
    ];
    
    const lines = markdown.split('\n');
    const filteredLines = lines.filter(line => {
        const trimmed = line.trim();
        return !disclaimerPatterns.some(pattern => pattern.test(trimmed));
    });
    
    filteredMarkdown = filteredLines.join('\n');
    const filteredLinesArray = filteredMarkdown.split('\n');
    const elements = [];
    let currentParagraph = [];
    let inList = false;
    let listItems = [];

    const renderParagraph = () => {
        if (currentParagraph.length > 0) {
            const text = currentParagraph.join(' ').trim();
            if (text) {
                elements.push(
                    <Typography key={`p-${elements.length}`} variant="body1" paragraph>
                        {parseInlineFormatting(text)}
                    </Typography>
                );
            }
            currentParagraph = [];
        }
    };

    const renderList = () => {
        if (listItems.length > 0) {
            elements.push(
                <Box key={`list-${elements.length}`} component="ul" sx={{ pl: 3, mb: 2 }}>
                    {listItems.map((item, idx) => (
                        <Typography key={idx} component="li" variant="body1" sx={{ mb: 0.5 }}>
                            {parseInlineFormatting(item)}
                        </Typography>
                    ))}
                </Box>
            );
            listItems = [];
        }
    };

    const parseInlineFormatting = (text) => {
        const parts = [];
        let currentIndex = 0;

        // Handle bold **text**
        const boldRegex = /\*\*([^*]+)\*\*/g;
        let match;
        let lastIndex = 0;

        while ((match = boldRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }
            parts.push(
                <Typography key={`bold-${currentIndex}`} component="span" fontWeight={600}>
                    {match[1]}
                </Typography>
            );
            lastIndex = match.index + match[0].length;
            currentIndex++;
        }

        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return parts.length > 0 ? parts : text;
    };

    filteredLinesArray.forEach((line, index) => {
        const trimmed = line.trim();

        // Headings
        if (trimmed.startsWith('# ')) {
            renderParagraph();
            renderList();
            inList = false;
            elements.push(
                <Typography key={`h1-${index}`} variant="h4" fontWeight={700} gutterBottom sx={{ mt: 3, mb: 2 }}>
                    {trimmed.substring(2)}
                </Typography>
            );
        } else if (trimmed.startsWith('## ')) {
            renderParagraph();
            renderList();
            inList = false;
            elements.push(
                <Typography key={`h2-${index}`} variant="h5" fontWeight={600} gutterBottom sx={{ mt: 2, mb: 1 }}>
                    {trimmed.substring(3)}
                </Typography>
            );
        } else if (trimmed.startsWith('### ')) {
            renderParagraph();
            renderList();
            inList = false;
            elements.push(
                <Typography key={`h3-${index}`} variant="h6" fontWeight={600} gutterBottom sx={{ mt: 1.5, mb: 1 }}>
                    {trimmed.substring(4)}
                </Typography>
            );
        } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            renderParagraph();
            if (!inList) {
                inList = true;
            }
            listItems.push(trimmed.substring(2));
        } else if (trimmed === '---' || trimmed === '***') {
            renderParagraph();
            renderList();
            inList = false;
            elements.push(<Divider key={`divider-${index}`} sx={{ my: 2 }} />);
        } else if (trimmed === '') {
            renderParagraph();
            renderList();
            inList = false;
        } else {
            if (inList) {
                renderList();
                inList = false;
            }
            currentParagraph.push(trimmed);
        }
    });

    renderParagraph();
    renderList();

    return (
        <Box 
            sx={{ 
                '& p': { mb: 1.5 }, 
                '& ul': { mb: 2 },
                '@media print': {
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                        pageBreakAfter: 'avoid',
                        breakAfter: 'avoid',
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid'
                    },
                    '& p': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                        orphans: 3,
                        widows: 3
                    }
                }
            }}
        >
            {elements}
        </Box>
    );
};

export default MarkdownRenderer;

